import { useEffect, useState, useRef } from "react"
import classNames from "classnames"
import image, { PRODUCT_PHOTOGRAMME } from "lacinetek-common/src/images"

import { useMainCategories } from "lib/tools/categories"
import { useTranslation } from "lib/i18n/translation"
import { useProducts, LIST_DISPLAY } from "lib/products"
import classification from "lib/tools/classification"

import ProductAccessLink from "components/common/product-access-link"
import SkeletonLink from "components/common/skeleton-link"
import Image from "components/common/image"
import { Tooltip } from "components/tooltip"

const Film = ({ film, style }) => {
	const itemRef = useRef(null)

	const { allLists } = useMainCategories()
	const { c } = useTranslation("common")
	const { displayMode } = useProducts()

	const [tiny, setTiny] = useState(true)
	const [tooltipStyle, setTooltipStyle] = useState({})

	const recommenders = film.recommendersIds
		.map(id => allLists.find(l => l.director.id == id))
		.filter(l => l !== undefined)
		.map(l => l.name)

	useEffect(() => {
		setTimeout(() => {
			setTiny(false)
		}, 200)
	}, [])

	useEffect(() => {
		const _width= displayMode === LIST_DISPLAY
			? itemRef.current?.offsetWidth / 1.2
			: itemRef.current?.offsetWidth

		setTooltipStyle({
			width: `${_width}px`
		})
	}, [displayMode, style])

	return (
		<div
			ref={itemRef}
			className={classNames(
				"item has-tooltip",
				{
					"in-selection": film.inMonthSelection,
					"unavailable": (!film.available || film.availableSoon) /*&& !hasAccess(film, user)*/,
					"soon": film.availableSoon
				}
			)}
			style={style}
		>
			<div className="item-content">
				<SkeletonLink
					href="/film/[id]"
					as={`/film/${film.linkRewrite}`}
					key={`film-link-${film.id}`}
				>
					<a title={c("see-single-film")} className="more-link">
						<span className="label">{c("see-single-film")}</span>
					</a>
				</SkeletonLink>
				<Info
					film={film}
					c={c}
					tiny={false}
				/>
				<a
					href="#"
					rel="nofollow noopener"
					onClick={e => e.preventDefault()}
					title={c("more-informations")}
					className="tooltip-toggle icon-about"
					aria-describedby={`tooltip-film-info-${film.id}`}
				>
					<span className="label">{c("more-informations")}</span>
				</a>
				<Tooltip
					id={`tooltip-film-info-${film.id}`}
					className={classNames("item inGrid", {"inList": displayMode === LIST_DISPLAY}) }
					positionRef={itemRef}
					style={tooltipStyle}
					align="start"
				>
					<TooltipContent
						film={film}
						recommenders={recommenders}
						c={c}
					/>
				</Tooltip>
			</div>

			{!tiny && (
				<nav className="film-actions" aria-label={c("film-actions")}>
					<ProductAccessLink product={film} />
				</nav>
			)}
		</div>
	)
}

export default Film

const Info = ({ film, c, tiny }) => (
	<div className="film-link">
		<figure className="cover">
			<picture>
				<Image
					src={image(film, PRODUCT_PHOTOGRAMME)}
					alt={film.name}
					className="cover"
				/>
			</picture>
		</figure>
		<div className="infos">
			<h3 className="title large">
				{film.name}
				{classification(film)}
				{film.inMonthSelection && <small className="film-event icon-selection">{c("month-selection")}</small>}
			</h3>

			{!tiny && (
				<>
					<span className="director large">
						{film.director}
						{!film.available
							? <small className="film-status">{c("not-available-yet")}</small>
							: film.availableSoon && <small className="film-status">{c("online-soon")}</small>
						}
						{film.acquisition && <small className="film-status">{c("new-acquisition")}</small>}
					</span>
					<span className="year">{film.year}</span>
					<span className="country">{film.origin}</span>
					{film.bonusesCount > 0 && (
						<ul className="has-bonus">
							<li className="exclusive">{c("bonus")}</li>
						</ul>
					)}
				</>
			)}
		</div>
	</div>
)

const TooltipContent = ({ film, recommenders, c }) => (
	<>
		<figure>
			<picture>
				<Image src={image(film, PRODUCT_PHOTOGRAMME)} alt={film.name} />
			</picture>
		</figure>
		<div>
			<span className="duration">{film.duration}</span>
			<p dangerouslySetInnerHTML={{ __html: film.description }} />

			{film.actors
				&& film.actors.length
				&& (
					<>
						<br />
						<span>{c("with")} {film.actors.map(actor => actor.name).join(', ')}</span>
					</>
				)
			}
		</div>

		{film.recommendersIds
			&& film.recommendersIds.length > 0
			&& (
				<p className="is-recommended">
					<b>{c("recommended-by")}</b> {recommenders.join(", ")}
				</p>
			)
		}
	</>
)
